import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../styles.modules.scss";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "../../../button";
import { useElementOnScreen } from "../../../../hooks/use-element-on-screen";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";
import { handleCTAClick } from "..";
import { parseCTAs } from "../parse-ctas";
import { usePollContext } from "../../../../stores/poll";
import {
    findRelatedMediaByTitle,
    findRelatedMediaLabelByTitle,
} from "../../../../utils/related-media";
import classNames from "classnames";
import { bridge } from "../../../../utils/bridge";
import { Timer } from "../campaign-parts/timer";

export type ProgressSlideType = {
    type: "progressTrackerEntry";
    id: string;
    title: string;
    tracker: ProgressTracker;
    milestones: ProgressMilestone[];
    ctas: InboxCTA[];
    targeting?: Targeting;
    lang: string;
    relatedMedia?: RelatedMedia[];
};

export const MangoParadoxProgressSlide = ({
    slide,
    previousDaySlide,
}: {
    slide: ProgressSlideType;
    previousDaySlide?: ProgressSlideType;
}) => {
    const { title, tracker, milestones, targeting, relatedMedia } = slide;

    const [ref, isVisible] = useElementOnScreen<HTMLDivElement>({
        threshold: 0.001,
    });
    const bottomElementRef = React.useRef<HTMLDivElement>(null);
    const firstLockedRef = React.useRef<HTMLDivElement>(null);
    const trackerProgressRef = React.useRef<HTMLDivElement>(null);
    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();
    const intl = useIntl();
    const {
        actions: { openModal },
    } = usePollContext();

    const dayNumber = Number(
        relatedMedia?.find((media) => media.title === "day-number")?.label ?? 0,
    );

    // TODO: Delete spire-crypt localStorage keys

    const hasSeenBefore =
        localStorage.getItem(`mango-paradox-seen-day-${dayNumber}`) === "true";

    const [startSequencePhase, setStartSequencePhase] = useState<
        "start" | "playing" | "end"
    >(
        dayNumber === 1 || previousDaySlide === undefined || hasSeenBefore
            ? "end"
            : "start",
    );

    const [previousRewardsIsOpen, setPreviousRewardsIsOpen] = useState(false);
    const togglePreviousRewardsIsOpen = () => {
        setPreviousRewardsIsOpen((prev) => !prev);
    };

    useEffect(() => {
        if (isVisible && startSequencePhase === "start") {
            setTimeout(() => {
                setStartSequencePhase("playing");
            }, 1000);

            setTimeout(() => {
                setStartSequencePhase("end");
                localStorage.setItem(
                    `mango-paradox-seen-day-${dayNumber}`,
                    "true",
                );
            }, 4000);
        }
    }, [startSequencePhase, isVisible]);

    const handleClick = handleCTAClick(
        sendCommand,
        openNewsArticle,
        openModal,
        title,
    );

    const relatedMediaStyles = useMemo(() => {
        const styles: React.CSSProperties & { [key: string]: string } = {};

        const imageMap = {
            icon: "--icon-image",
            "modifier-icon": "--modifier-icon",
            "background-layer": "--background-layer-image",
            ray: "--ray",
            "reward-1": "--reward-1-image",
            "reward-2": "--reward-2-image",
            "reward-3": "--reward-3-image",
        };

        const labelMap = {
            "background-layer": "--background-color",
        };

        Object.entries(imageMap).forEach(([title, cssVar]) => {
            const imageUrl = findRelatedMediaByTitle(relatedMedia ?? [], title);
            if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
        });

        Object.entries(labelMap).forEach(([title, cssVar]) => {
            const label = findRelatedMediaLabelByTitle(
                relatedMedia ?? [],
                title,
            );
            if (label) styles[cssVar] = label;
        });

        return styles;
    }, [relatedMedia]);

    const previousDayRelatedMediaStyles = useMemo(() => {
        const styles: React.CSSProperties & { [key: string]: string } = {};

        const imageMap = {
            "background-layer": "--previous-day-background-layer-image",
        };

        const labelMap = {
            "background-layer": "--previous-day-background-color",
        };

        Object.entries(imageMap).forEach(([title, cssVar]) => {
            const imageUrl = findRelatedMediaByTitle(
                previousDaySlide?.relatedMedia ?? [],
                title,
            );
            if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
        });

        Object.entries(labelMap).forEach(([title, cssVar]) => {
            const label = findRelatedMediaLabelByTitle(
                previousDaySlide?.relatedMedia ?? [],
                title,
            );
            if (label) styles[cssVar] = label;
        });

        return styles;
    }, [previousDaySlide?.relatedMedia]);

    React.useEffect(() => {
        if (bottomElementRef.current && isVisible) {
            const top = bottomElementRef.current.offsetTop;
            ref.current?.parentElement?.scrollTo({
                top,
            });
        }
    }, [bottomElementRef.current, isVisible]);

    //

    React.useEffect(() => {
        setTimeout(() => {
            if (firstLockedRef.current && isVisible) {
                // Scroll the top of the filled bar to the center of the view
                const trackerProgressOffsetTop =
                    trackerProgressRef.current?.offsetTop ?? 0;
                const trackersOffsetTop =
                    firstLockedRef.current.parentElement?.parentElement
                        ?.offsetTop ?? 0;
                const trackersMarginTop = firstLockedRef.current.parentElement
                    ?.parentElement
                    ? parseFloat(
                          getComputedStyle(
                              firstLockedRef.current.parentElement
                                  ?.parentElement,
                          ).marginTop,
                      )
                    : 0;
                const contentPaddingTop = firstLockedRef.current.parentElement
                    ?.parentElement?.parentElement
                    ? parseFloat(
                          getComputedStyle(
                              firstLockedRef.current.parentElement
                                  ?.parentElement?.parentElement,
                          ).paddingTop,
                      )
                    : 0;

                const top =
                    trackerProgressOffsetTop +
                    trackersOffsetTop +
                    trackersMarginTop +
                    contentPaddingTop -
                    (ref.current?.parentElement?.clientHeight ?? 0) / 1.5;

                ref.current?.parentElement?.scrollTo({
                    top,
                    behavior: "smooth",
                });
            }
        }, 500);
    }, [bottomElementRef.current, isVisible]);

    const timerString =
        startSequencePhase === "start" || startSequencePhase === "playing"
            ? previousDaySlide?.relatedMedia?.find(
                  (media) => media.title === "timer",
              )?.label
            : relatedMedia?.find((media) => media.title === "timer")?.label;

    const rewards =
        relatedMedia
            ?.find((media) => media.title === "day-rewards")
            ?.label?.split(",") ?? [];

    const ctas = parseCTAs(slide.ctas, handleClick);

    const fillPercentage = tracker.progress;
    const barColor = tracker.color;

    const style = {
        width: `${Math.max(fillPercentage, 1.25)}%`,
    };

    const previousDayStyle = {
        width: `${Math.max(previousDaySlide?.tracker?.progress ?? 0, 1.25)}%`,
    };

    const midWayStyle = {
        width: "1.25%",
    };

    const hasMileStoneLabels = milestones.some(
        (milestone) => !!milestone.label,
    );

    const barStyle = {
        "--bar-color": barColor,
        "--previous-day-bar-color": previousDaySlide?.tracker.color ?? barColor,
        height: "100%",
        ...relatedMediaStyles,
        ...previousDayRelatedMediaStyles,
    } as React.CSSProperties;

    const translate = (id: "previousRewards" | "dayReward" | "noReward") => {
        type Translations = {
            previousRewards: string;
            dayReward: string;
            noReward: string;
        };

        const translations: {
            [key: string]: Translations;
        } = {
            de: {
                previousRewards: "Vorherige Belohnungen",
                dayReward: `Belohnung – Tag ${dayNumber}`,
                noReward: "Keine Belohnung",
            },
            en: {
                previousRewards: "Previous Rewards",
                dayReward: `Day ${dayNumber} Reward`,
                noReward: "No Reward",
            },
            es: {
                previousRewards: "Recompensas anteriores",
                dayReward: `Recompensa del día ${dayNumber}`,
                noReward: "Ninguna recompensa",
            },
            fr: {
                previousRewards: "Récompenses précédentes",
                dayReward: `Récompense du jour ${dayNumber}`,
                noReward: "Aucune récompense",
            },
            it: {
                previousRewards: "Premi precedenti",
                dayReward: `Premio del ${dayNumber}º giorno`,
                noReward: "Nessuna ricompensa",
            },
            ja: {
                previousRewards: "前の報酬",
                dayReward: `${dayNumber}日目の報酬`,
                noReward: "報酬なし",
            },
            ko: {
                previousRewards: "이전 보상",
                dayReward: `${dayNumber}일차 보상`,
                noReward: "보상 없음",
            },
            pl: {
                previousRewards: "Poprzednie nagrody",
                dayReward: `Nagroda za dzień ${dayNumber}`,
                noReward: "Brak nagrody",
            },
            pt: {
                previousRewards: "Recompensas anteriores",
                dayReward: `Recompensa do dia ${dayNumber}`,
                noReward: "Nenhuma recompensa",
            },
            ru: {
                previousRewards: "Предыдущие награды",
                dayReward: `Награда ${dayNumber}-го дня`,
                noReward: "Нет награды",
            },
            tr: {
                previousRewards: "Önceki Ödüller",
                dayReward: `${dayNumber}. Gün Ödülü`,
                noReward: "Ödül yok",
            },
            "zh-hans": {
                previousRewards: "先前奖励",
                dayReward: `第${dayNumber}天奖励`,
                noReward: "无奖励",
            },
            zh: {
                previousRewards: "先前獎勵",
                dayReward: `第${dayNumber}天獎勵`,
                noReward: "無獎勵",
            },
        };

        if (translations[slide.lang] === undefined) {
            return translations["en"]?.[id];
        }

        return translations[slide.lang]?.[id];
    };

    const previousDayReached =
        previousDaySlide !== undefined &&
        previousDaySlide.tracker.progress === 100 &&
        rewards[dayNumber - 2] !== undefined;

    const showPreviousDayPrize =
        previousDaySlide !== undefined &&
        rewards.length > 0 &&
        ((previousDayReached && rewards[dayNumber - 2] !== "locked") ||
            !previousDayReached);

    const formattedModifierText = (id: string) => {
        const text = intl.formatMessage({ id });

        if (id === "globalText.langMangoParadoxModifierDay2") {
            if (slide.lang === "en") {
                const words = text.split(" ");

                return (
                    <>
                        {words[0]}&nbsp;{words.slice(1).join(" ")}
                    </>
                );
            }
        }

        if (id === "globalText.langMangoParadoxModifierDay3") {
            if (slide.lang === "en") {
                const words = text.split(" ");

                return (
                    <>
                        {words.slice(0, -1).join("\u00A0") +
                            " " +
                            words[words.length - 1]}
                    </>
                );
            }
        }

        if (id === "globalText.langMangoParadoxModifierDay6") {
            if (slide.lang === "en") {
                const words = text.split(" ");

                return <>{words.join("\u00A0")}</>;
            }
        }

        if (id === "globalText.langMangoParadoxModifierDay7") {
            if (slide.lang === "en") {
                const words = text.split(" ");

                return (
                    <>
                        {words[0]}
                        <br />
                        {words[1]}
                    </>
                );
            }
        }
        if (id === "globalText.langMangoParadoxModifierDay10") {
            if (slide.lang === "en") {
                const words = text.split(" ");

                return (
                    <>
                        {words[0]}&nbsp;{words.slice(1).join(" ")}
                    </>
                );
            }
        }

        return text;
    };

    return (
        <>
            <div
                className={`slide progress-indicator ${styles.slide} ${
                    isVisible ? "visible" : ""
                } ${hasMileStoneLabels ? "with-milestone-labels" : ""} ${
                    targeting?.campaignId ?? ""
                } without-title ${startSequencePhase} ${slide.lang}`}
                ref={ref}
                style={barStyle}
            >
                {showPreviousDayPrize && (
                    <div className="previous-day-wrapper">
                        <div className="previous-day-text">
                            <div className={`title ${styles.title} `}>
                                {translate("dayReward")}
                            </div>
                        </div>
                        <div className="previous-day-reward-wrapper">
                            <div
                                className={`previous-day-reward ${!previousDayReached ? "no-reward" : ""}`}
                                data-reward={
                                    previousDayReached
                                        ? rewards[dayNumber - 2]
                                        : undefined
                                }
                            ></div>
                        </div>
                    </div>
                )}
                <div
                    className={`background-layer ${styles["background-layer"]}`}
                ></div>
                {timerString !== undefined && (
                    <div className="timer-container">
                        <div className="timer-label">
                            <FormattedMessage id="globalText.langMangoParadoxTimerLabel" />
                        </div>
                        <Timer
                            timerString={timerString}
                            lang={String(slide.lang)}
                        />
                    </div>
                )}
                <div className={`modifier modifier-${dayNumber}`}>
                    <div className="icon"></div>
                    <div className="text-wrapper">
                        <div className="text">
                            {formattedModifierText(
                                `globalText.langMangoParadoxModifierDay${dayNumber}`,
                            )}
                        </div>
                    </div>
                </div>
                <div className={`content ${styles.content}`}>
                    {title && (
                        <>
                            <div className={`filter ${styles.filter}`} />

                            <div
                                className={`title ${styles.title} `}
                                data-title={title}
                            >
                                {startSequencePhase === "start" ||
                                startSequencePhase === "playing"
                                    ? previousDaySlide?.title
                                    : title}
                            </div>
                        </>
                    )}
                    <div className={`trackers ${styles.trackers}`}>
                        <div className={`rewards ${styles.rewards}`}>
                            <div className={`reward ${styles.reward}`}></div>
                            <div className={`reward ${styles.reward}`}></div>
                            <div className={`reward ${styles.reward}`}></div>
                        </div>
                        <div className={`rays ${styles.rays}`}>
                            <div className={`ray ${styles.ray}`}></div>
                            <div className={`ray ${styles.ray}`}></div>
                            <div className={`ray ${styles.ray}`}></div>
                        </div>
                        {isVisible && (
                            <>
                                <div
                                    className={`milestone-label-wrapper ${styles["milestone-label-wrapper"]}`}
                                >
                                    <div
                                        className={`milestone-label ${styles["milestone-label"]}`}
                                    >
                                        {startSequencePhase === "start" ||
                                        startSequencePhase === "playing" ? (
                                            <FormattedMessage
                                                id={`globalText.langMangoParadoxGoalDay${dayNumber - 1}`}
                                            />
                                        ) : (
                                            <FormattedMessage
                                                id={`globalText.langMangoParadoxGoalDay${dayNumber}`}
                                            />
                                        )}
                                    </div>
                                    {((startSequencePhase === "start" ||
                                        startSequencePhase === "playing") &&
                                    previousDaySlide !== undefined
                                        ? previousDaySlide.milestones
                                        : milestones
                                    ).map((milestone, index) => {
                                        if (!milestone.label) {
                                            return <div key={index} />;
                                        }

                                        const milestonePosition = Math.max(
                                            milestone.progress,
                                            5, // 5% is the minimum we support without the UI looking totally wack
                                        );

                                        const hasReachedMilestone =
                                            milestone.unlocked;

                                        const style = {
                                            left: `${milestonePosition}%`,
                                        };

                                        return (
                                            <div
                                                key={index}
                                                style={style}
                                                className={`milestone-label ${
                                                    styles["milestone-label"]
                                                } ${
                                                    hasReachedMilestone
                                                        ? styles[
                                                              "milestone-reached"
                                                          ]
                                                        : styles[
                                                              "milestone-unreached"
                                                          ]
                                                } ${
                                                    hasReachedMilestone
                                                        ? "milestone-reached"
                                                        : "milestone-unreached"
                                                }`}
                                                title={milestone.label}
                                            >
                                                {milestone.label}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="tracker-dice-wrapper">
                                    <div className={`tracker-wrapper`}>
                                        <div
                                            className={`tracker ${styles.tracker} ${tracker.progress === 100 ? "full" : ""}`}
                                        >
                                            <div
                                                style={
                                                    startSequencePhase ===
                                                    "start"
                                                        ? previousDayStyle
                                                        : startSequencePhase ===
                                                            "playing"
                                                          ? midWayStyle
                                                          : style
                                                }
                                                className={`tracker-progress ${
                                                    styles["tracker-progress"]
                                                } ${
                                                    fillPercentage > 99
                                                        ? "almost-complete"
                                                        : ""
                                                } ${
                                                    startSequencePhase ===
                                                    "start"
                                                        ? "previous-day"
                                                        : ""
                                                }`}
                                                ref={trackerProgressRef}
                                            >
                                                {(startSequencePhase ===
                                                    "start" ||
                                                    startSequencePhase ===
                                                        "playing") &&
                                                previousDaySlide !==
                                                    undefined ? (
                                                    <RawValue
                                                        tracker={
                                                            previousDaySlide.tracker
                                                        }
                                                        trackerProgressRef={
                                                            trackerProgressRef
                                                        }
                                                    />
                                                ) : (
                                                    <RawValue
                                                        tracker={tracker}
                                                        trackerProgressRef={
                                                            trackerProgressRef
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div
                                            className={`icon-container ${styles["icon-container"]}`}
                                            style={
                                                startSequencePhase === "start"
                                                    ? previousDayStyle
                                                    : startSequencePhase ===
                                                        "playing"
                                                      ? midWayStyle
                                                      : style
                                            }
                                        >
                                            <div
                                                className={`icon ${styles.icon} icon-${dayNumber}`}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <div ref={bottomElementRef}></div>
                </div>
                <div className="previous-day-background"></div>
            </div>
            <div className={`wrapper ${styles.wrapper}`}>
                {ctas.length ? (
                    <div
                        className={`ctas ${
                            ctas.length === 1 ? "single" : "multi"
                        } ${styles.ctas} ${startSequencePhase}`}
                    >
                        {ctas.map(({ label, variant, onClick }, index) => {
                            return (
                                <Button
                                    key={index}
                                    onClick={onClick}
                                    variant={variant}
                                >
                                    <span className="buttonTextWrapper">
                                        {label || (
                                            <FormattedMessage id="globalText.langGo" />
                                        )}
                                    </span>
                                </Button>
                            );
                        })}
                    </div>
                ) : null}
            </div>
            {dayNumber !== 1 && rewards.length > 0 && (
                <div
                    className={classNames("previousRewardsPopover", {
                        isOpen: previousRewardsIsOpen,
                        isVisible: startSequencePhase === "end",
                    })}
                >
                    <span className="previousRewardsPopoverRewardsLabel">
                        {translate("previousRewards")}
                    </span>

                    <div className="rewards">
                        {rewards.map((reward, index) => (
                            <div
                                className={`${reward} ${reward !== "locked" && reward !== "no" ? "earned" : ""} ${index === dayNumber - 2 ? "latest" : ""}`} // We set the latest class to the previous day's reward, by removing 2 from the dayNumber
                                key={index}
                                data-reward={reward}
                                onClick={async () => {
                                    (await bridge()).openShop();
                                }}
                            ></div>
                        ))}
                    </div>

                    <div
                        className="previousRewardsPopoverHandle"
                        onClick={togglePreviousRewardsIsOpen}
                    >
                        <div className="arrow"></div>
                    </div>
                </div>
            )}
        </>
    );
};

function RawValue({
    tracker,
    trackerProgressRef,
}: {
    tracker: ProgressTracker;
    trackerProgressRef: React.RefObject<HTMLDivElement | null>;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [fitsLeft, setFitsLeft] = useState<boolean>(false);

    useEffect(() => {
        if (!ref.current || !trackerProgressRef.current) return;

        setFitsLeft(
            ref.current.clientWidth + 20 <
                trackerProgressRef.current.clientWidth,
        );
    }, []);

    if (!tracker.rawProgressInMillions) return null;

    let unit = "M";
    let value = tracker.rawProgressInMillions;

    if (tracker.rawProgressInMillions >= 1000000) {
        unit = "T";
        value = Number((tracker.rawProgressInMillions / 1000000).toFixed(2));
    } else if (tracker.rawProgressInMillions >= 1000) {
        unit = "B";
        value = Number((tracker.rawProgressInMillions / 1000).toFixed(2));
    }

    return (
        <div
            className="rawValue"
            ref={ref}
            style={{
                left: !fitsLeft ? "calc(100% + 1.4rem)" : "0.6rem",
            }}
        >
            <div className="rawValueText">
                {value}
                <FormattedMessage
                    id={`globalText.langMangoParadoxUnit${unit}`}
                />
            </div>
        </div>
    );
}
